import { useEffect, useState } from "react";
import NewSelectInput from "../../../components/NewSelectInput";
import {
  IconRefresh,
  IconTruck,
  IconCalendar,
  IconClipboardText,
} from "@tabler/icons-react";
import NewinputField from "../../../components/NewInput";
import { Control, UseFormClearErrors } from "react-hook-form";
import { useMerchantAllListData } from "../../../apis/merchantApis";
import { useFetchLmClientOrdersData,
  usePickupLocationClientAddressData,
} from "../../../apis/pickupApis";
import ProductTable from "../../DRSManagement/_components/ProductTable";
import { useLanguage } from "../../../context/LanguageContext";

const vehicleTypes = [
  { id: 1, value: "car", text: "Car" },
  { id: 2, value: "van", text: "Van" },
  { id: 3, value: "dokker", text: "DoKker" },
];

const RequestTypeData = [
  { id: 1, value: "Shipment", text: "Request with Shipment" },
  { id: 2, value: "NonShipment", text: "Request without Shipment" },
];

type Props = {
  register: any;
  setValue: any;
  watch: any;
  errors: any;
  clearErrors?: UseFormClearErrors<any>;
  control: Control<any>;
  reset?: any;
  setSelectedOrders: React.Dispatch<React.SetStateAction<string[]>>;
  selectedOrders: string[];
  step?: number;
};

const SectionHeader = ({
  icon: Icon,
  title,
  className = "border-b border-[#FBD037]/30 pb-2 mb-3",
}: {
  icon: any;
  title: string;
  className?: string;
}) => (
  <div className={`flex items-center gap-2 ${className}`}>
    <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
      <Icon size={16} className="text-[#212121]" stroke={1.5} />
    </div>
    <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
      {title}
    </p>
  </div>
);

const GeneratePickupModal = ({
  register,
  errors,
  setValue,
  clearErrors,
  watch,
  selectedOrders,
  setSelectedOrders,
  step,
}: Props) => {
  const { t } = useLanguage();
  const [awbText, setAwbText] = useState("");
  const [searchFor, setSearchFor] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(1000000000000);
  const [loadOrder, setLoadOrder] = useState(false);

  const [orders, setOrders] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const { data: clients } = useMerchantAllListData();

  const { data: clientAddresses } = usePickupLocationClientAddressData(
    watch("clientId") || "",
  );
  const { data: ClientOrders, refetch } = useFetchLmClientOrdersData(
    pageNo,
    limit,
    searchFor,
    watch("clientId") || "",
    loadOrder,
  );

  useEffect(() => {
    setOrders([]);
    setPageNo(1);
    setHasMore(true);

    if (watch("clientId") && loadOrder) {
      refetch();
    }
  }, [watch("clientId"), loadOrder]);

  // Only clear locationId when clientId (merchant) changes, not when loadOrder changes
  useEffect(() => {
    setValue("locationId", undefined);
  }, [watch("clientId")]);

  useEffect(() => {
    if (!ClientOrders) return;

    const newData = ClientOrders?.pickupShipments || [];

    if (pageNo === 1) {
      setOrders(newData);
    } else {
      setOrders((prev: any[]) => [...prev, ...newData]);
    }

    // setHasMore(newData.length >= limit);
    setIsFetchingMore(false);
  }, [ClientOrders]);

  useEffect(() => {
    const scrollContainer = document.querySelector(".pickup-scroll");
    if (!scrollContainer) return;

    const onScroll = () => {
      if (!hasMore || isFetchingMore) return;

      const isBottom =
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
        scrollContainer.scrollHeight - 80;

      if (isBottom) {
        setIsFetchingMore(true);
        setPageNo((prev: number) => prev + 1);
      }
    };

    scrollContainer.addEventListener("scroll", onScroll);
    return () => scrollContainer.removeEventListener("scroll", onScroll);
  }, [hasMore, isFetchingMore]);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  // paste wise
  const [textareaValue, setTextareaValue] = useState("");

  // paste wise End

  // Default value
  useEffect(() => {
    setValue("requestType", "PICKUP");
    setValue("pickupType", "Shipment");
  }, []);

  // Refetch orders when client changes
  useEffect(() => {
    refetch();
  }, [watch("clientId")]);

  // ✅ FIX: Clear pickup-only fields when switching to DROPOFF
  useEffect(() => {
    const requestType = watch("requestType");
    if (requestType === "DROPOFF") {
      setValue("vehicleType", undefined);
      setValue("locationId", undefined);
    }
  }, [watch("requestType")]);

  const showShipmentsStep = true;
  const isStepperActive = typeof step === "number";

  return (
    <div className="pickup-scroll space-y-2.5">
      {/* Stepper Header */}
      {isStepperActive && showShipmentsStep && (
        <div className="flex items-center justify-between relative px-3 py-1.5 mb-1.5 bg-[#FCFDF8] rounded-xl border border-yellow-100 shadow-sm select-none">
          {/* Step 1 */}
          <div className="flex items-center gap-2 z-10">
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black transition-all duration-300 ${
                step >= 1
                  ? "bg-[#FBD037] text-slate-900 ring-4 ring-yellow-100"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              {step > 1 ? "✓" : "1"}
            </div>
            <span
              className={`text-[11px] font-bold ${step >= 1 ? "text-slate-800" : "text-slate-400"}`}
            >
              {t("Details")}
            </span>
          </div>

          {/* Connector */}
          <div
            className={`flex-1 h-0.5 mx-2.5 transition-all duration-300 ${step >= 2 ? "bg-[#FBD037]" : "bg-slate-200"}`}
          />

          {/* Step 2 */}
          <div className="flex items-center gap-2 z-10">
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black transition-all duration-300 ${
                step >= 2
                  ? "bg-[#FBD037] text-slate-900 ring-4 ring-yellow-100"
                  : "bg-slate-200 text-slate-500"
              }`}
            >
              2
            </div>
            <span
              className={`text-[11px] font-bold ${step >= 2 ? "text-slate-800" : "text-slate-400"}`}
            >
              {t("Shipments")}
            </span>
          </div>
        </div>
      )}

      {/* Step 1: Shipment Details & Schedule (Combined) */}
      {(!isStepperActive || step === 1) && (
        <>
          {/* Shipment Details */}
          <div className="relative group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-1.5">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
            <div className="flex items-center justify-between border-b border-[#FBD037]/30 pb-2 mb-3">
              <SectionHeader
                icon={IconTruck}
                title={t("Generate Request")}
                className="w-full mb-0 pb-0 border-none"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <NewSelectInput
                className=""
                label={t("Merchant")}
                name="clientId"
                value={watch("clientId")}
                register={register}
                clearErrors={clearErrors}
                error={errors?.clientId}
                validation={{ required: "Please select Client" }}
                onChange={(val: any) => {
                  setValue("clientId", val);
                }}
                selectOptions={
                  clients?.map((client: any) => ({
                    id: client?.id,
                    value: client?.id,
                    text: client?.name,
                  })) || []
                }
              />
              <NewSelectInput
                className=""
                label={t("Mode")}
                name="requestType"
                clearErrors={clearErrors}
                value={watch("requestType")}
                register={register}
                error={errors?.requestType}
                validation={{ required: "Please select Pickup Type" }}
                onChange={(val: any) => {
                  setValue("requestType", val);
                }}
                selectOptions={
                  ["PICKUP", "DROPOFF"]?.map((type: any) => ({
                    id: type,
                    value: type,
                    text: type,
                  })) || []
                }
              />

              {/* Vehicle Type (only for PICKUP) */}
              {watch("requestType") === "PICKUP" && (
                <NewSelectInput
                  className=""
                  label={t("Vehicle")}
                  name="vehicleType"
                  value={watch("vehicleType")}
                  clearErrors={clearErrors}
                  register={register}
                  error={errors?.vehicleType}
                  validation={{ required: "Please select vehicle Type" }}
                  onChange={(val: any) => {
                    setValue("vehicleType", val);
                  }}
                  selectOptions={vehicleTypes}
                />
              )}

              {/* Location (only for PICKUP) */}
              {watch("requestType") === "PICKUP" && (
                <NewSelectInput
                  className=""
                  label={t("Location")}
                  name="locationId"
                  value={watch("locationId")}
                  register={register}
                  clearErrors={clearErrors}
                  error={errors?.locationId}
                  validation={{ required: "Please select location" }}
                  onChange={(val: any) => {
                    setValue("locationId", val);
                  }}
                  selectOptions={
                    clientAddresses?.map((client: any) => ({
                      id: client?.id,
                      value: client?.id,
                      text:
                        client?.locationName ||
                        client?.fullAddress ||
                        client?.shortAddress ||
                        client?.full_address ||
                        client?.short_address ||
                        "",
                    })) || []
                  }
                />
              )}

              <NewinputField
                className=""
                type="date"
                name="pickupDate"
                title={t("Date")}
                placeholder="Enter Driver Contact"
                register={register}
                error={errors?.pickupDate}
                validation={{ required: "Pickup Date Required" }}
                min={new Date().toISOString().split("T")[0]}
              />

              <NewinputField
                className=""
                type="time"
                name="timeSlot"
                title={t("Time")}
                placeholder="Enter Driver Contact"
                register={register}
                error={errors?.timeSlot}
                validation={{ required: "Pickup Time Required" }}
                min={
                  watch("pickupDate") === new Date().toISOString().split("T")[0]
                    ? new Date().toISOString().slice(11, 16)
                    : undefined
                }
              />

              <div className="flex flex-col gap-1 col-span-3">
                <label className="text-[#1E293B] text-xs">{t("Instructions")}</label>
                <textarea
                  placeholder={t("paste order ids here…")}
                  {...register("instructions")}
                  className="w-full   rounded-xl resize-none py-[6px] px-4 outline-none bg-[#F7F6F2] border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-[#FBD037] transition-all"
                />
              </div>
            </div>
          </div>

          {/* Schedule & Info */}
          {/* <div className="relative group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-2 transition-all duration-300 space-y-1.5">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
              <SectionHeader icon={IconCalendar} title="Schedule & Instructions" />
              <div className="grid grid-cols-2 gap-4">
                <NewinputField
                  type="date"
                  name="pickupDate"
                  title="Preferred Date"
                  placeholder="Enter Driver Contact"
                  register={register}
                  error={errors?.pickupDate}
                  validation={{ required: "Pickup Date Required" }}
                  min={new Date().toISOString().split("T")[0]}
                />

                <NewinputField
                  type="time"
                  name="timeSlot"
                  title="Preferred Time"
                  placeholder="Enter Driver Contact"
                  register={register}
                  error={errors?.timeSlot}
                  validation={{ required: "Pickup Time Required" }}
                  min={
                    watch("pickupDate") === new Date().toISOString().split("T")[0]
                      ? new Date().toISOString().slice(11, 16)
                      : undefined
                  }
                />

                <div className="space-y-1 col-span-2">
                  <label className="text-[#1E293B] text-sm">Instructions</label>
                  <textarea
                    placeholder="Write here..."
                    {...register("instructions")}
                    className="w-full h-20 rounded-xl py-[6px] px-4 outline-none bg-white border border-[#D8D8D8] text-[#1E1E1E] text-sm focus:border-[#FBD037] transition-all"
                  />
                </div>

                <div className="space-y-1 col-span-2">
                  <div className="flex items-center py-2">
                    <label className="relative flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        id="pickupTypeShipment"
                        checked={watch("pickupType") === "Shipment"}
                        onChange={(e) => {
                          const newValue = e.target.checked
                            ? "Shipment"
                            : "NonShipment";
                          setValue("pickupType", newValue);
                          clearErrors?.("pickupType");
                        }}
                        className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] transition-all bg-white checked:bg-[#FBD037] checked:text-white"
                      />
                      <span className="absolute text-xs font-bold text-white peer-checked:opacity-100 opacity-0 transition-opacity duration-200 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                        ✓
                      </span>
                    </label>
                    <label htmlFor="pickupTypeShipment" className="ml-2 text-black cursor-pointer text-sm font-medium">
                      Add Shipment
                    </label>
                  </div>
                  {errors?.pickupType && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.pickupType.message}
                    </p>
                  )}
                </div>
              </div>
            </div> */}
        </>
      )}

      {/* Step 2: Shipment List */}
      {((isStepperActive && step === 2) ||
        (!isStepperActive && showShipmentsStep)) &&
        showShipmentsStep && (
          <div className="relative group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 border border-[#D2D2D2]/70 p-4 transition-all duration-300 space-y-1">
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/20 to-transparent pointer-events-none" />
            <div className="flex justify-between items-center gap-2 flex-wrap border-b border-[#FBD037]/30 pb-2 mb-3">
              <SectionHeader
                className=""
                icon={IconClipboardText}
                title={`${t("Shipment List")} (${orders.length})`}
              />

              <button
                type="button"
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-lg bg-[#FBD037] hover:bg-yellow-400 text-slate-800 transition active:scale-95 shadow-sm"
              >
                {!loadOrder ? (
                  <div
                    onClick={() => setLoadOrder(true)}
                    className="flex items-center space-x-1.5"
                  >
                    <IconRefresh stroke={1.5} size={14} />
                    <span>{t("Load Order")}</span>
                  </div>
                ) : (
                  <span onClick={() => setLoadOrder(false)}>{t("Paste AWB")}</span>
                )}
              </button>
            </div>
            <div className="w-full py-2">
              {/* ---- Load Order TABLE LIST ---- */}
              {loadOrder && (
                <div className="max-h-[180px] overflow-y-auto border border-gray-100 rounded-xl scrollbar-clean">
                  <ProductTable
                    orders={orders}
                    selectedOrders={selectedOrders}
                    setSelectedOrders={setSelectedOrders}
                  />

                  {/* Infinite scroll loader */}
                  {isFetchingMore && (
                    <div className="flex justify-center py-1 text-[11px] text-gray-500">
                      {t("loading more…")}
                    </div>
                  )}

                  {/* Over */}
                  {!hasMore && orders.length > 0 && (
                    <div className="flex justify-center py-1 text-[11px] text-gray-400">
                      {t("no more shipments")}
                    </div>
                  )}
                </div>
              )}

              {/* ---- Paste AWB Section ---- */}
              {!loadOrder && (
                <div className="bg-[#FCFDF8] border border-[#D2D2D2] rounded-xl p-2 h-[70px]">
                  <textarea
                    value={textareaValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      setTextareaValue(value);

                      const parsed = value
                        .split(/[\n,\t]+/)
                        .map((o) => o.trim())
                        .filter((o) => o.length > 0);

                      const deduped = Array.from(new Set(parsed));
                      setSelectedOrders(deduped);
                    }}
                    placeholder={t("paste order ids here…")}
                    className="w-full h-full resize-none outline-none bg-white text-xs text-[#212121]/70 font-mono"
                    rows={2}
                  />
                </div>
              )}
            </div>
          </div>
        )}
    </div>
  );
};

export default GeneratePickupModal;
